import {
  ChangeDetectionStrategy,
  Component,
  input,
  output,
  ViewChild,
  ElementRef,
  effect,
} from '@angular/core';
import { TemplateVariable } from '../../interfaces/template-variable.interface';
import { SafeHtmlPipe } from '../../pipes/safe-html.pipe';
import { getIconForType } from '../../utils/icon-helper';

/**
 * Componente de UI que renderiza a lista de sugestões em um dropdown.
 * É um componente "burro" que apenas exibe os dados recebidos via inputs.
 */
@Component({
  selector: 'app-suggestion-list',
  templateUrl: './suggestion-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SafeHtmlPipe],
})
export class SuggestionListComponent {
  /**
   * Referência ao elemento `ul` da lista para manipulação de rolagem.
   */
  @ViewChild('dropdownList') public dropdownListRef!: ElementRef<HTMLUListElement>;

  // --- Entradas (Inputs) ---
  /**
   * A lista de variáveis a serem exibidas.
   */
  public suggestions = input.required<TemplateVariable[]>();

  /**
   * Sinaliza se os dados estão sendo carregados (para exibir um spinner/mensagem).
   */
  public isLoading = input.required<boolean>();

  /**
   * O índice do item que deve ser destacado como ativo.
   */
  public activeIndex = input.required<number>();

  // --- Saídas (Outputs) ---
  /**
   * Evento emitido quando um item da lista é selecionado (clicado).
   */
  public itemSelected = output<TemplateVariable>();

  /**
   * Referência à função utilitária para obter ícones baseados no tipo da variável.
   */
  public getIcon = getIconForType;

  public constructor() {
    // Efeito que observa mudanças no `activeIndex` para rolar a lista automaticamente,
    // garantindo que o item ativo esteja sempre visível.
    effect(() => {
      const activeIndex = this.activeIndex();
      const dropdownElement = this.dropdownListRef?.nativeElement;
      if (dropdownElement && dropdownElement.children.length > activeIndex) {
        // Encontra o elemento DOM do item ativo pelo seu atributo `data-index`.
        const activeItem = dropdownElement.querySelector(`[data-index="${activeIndex}"]`);
        // Rola o item para a vista. `block: 'nearest'` evita rolagem desnecessária se o item já estiver visível.
        activeItem?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    });
  }

  /**
   * Lida com o evento `mousedown` em um item da lista.
   * `mousedown` é usado em vez de `click` para que o evento dispare antes do evento `blur` do editor,
   * prevenindo que o dropdown feche antes da seleção ser registrada.
   *
   * @param event O evento do mouse.
   * @param variable A variável associada ao item clicado.
   */
  public onItemMouseDown(event: MouseEvent, variable: TemplateVariable): void {
    event.preventDefault(); // Previne que o editor perca o foco.
    this.itemSelected.emit(variable);
  }
}
